import React, { useRef } from 'react';
import { Modal, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { expense } from '../../model/expense';
import { formatNumber } from '../../utils/util';
import { printDocument } from '../../utils/print-utils';

interface Props {
    open: boolean;
    expense: expense;
    onClose: () => void;
}

// Convert Number to Thai Baht Text
export const thaiBahtText = (num: number): string => {
    if (isNaN(num) || num === 0) return 'ศูนย์บาทถ้วน';
    if (num < 0) return 'ลบ' + thaiBahtText(Math.abs(num));

    const numbers = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
    const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

    const convertInteger = (str: string): string => {
        let text = '';
        const len = str.length;
        for (let i = 0; i < len; i++) {
            const digit = parseInt(str[i], 10);
            const pos = len - i - 1;
            if (digit !== 0) {
                if (pos === 1 && digit === 1) {
                    text += 'สิบ';
                } else if (pos === 1 && digit === 2) {
                    text += 'ยี่สิบ';
                } else if (pos === 0 && digit === 1 && len > 1 && parseInt(str[len - 2], 10) !== 0) {
                    text += 'เอ็ด';
                } else {
                    text += numbers[digit] + positions[pos % 6];
                }
            }
            if (pos % 6 === 0 && pos > 0) {
                text += 'ล้าน';
            }
        }
        return text;
    };

    const parts = num.toFixed(2).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    let result = convertInteger(integerPart) + 'บาท';
    if (decimalPart === '00') {
        result += 'ถ้วน';
    } else {
        result += convertInteger(decimalPart) + 'สตางค์';
    }
    return result;
};

const ExpenseVoucherPrint: React.FC<Props> = ({ open, expense, onClose }) => {
    const voucherRef = useRef<HTMLDivElement>(null);

    const handlePrint = () => {
        printDocument(voucherRef.current, {
            documentType: 'a4-portrait',
            title: `ใบสำคัญจ่ายเงิน ${expense.doc_no || ''}`
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={820}
            centered
            footer={[
                <Button key="close" onClick={onClose} data-testid="btn-close-voucher-modal">
                    ปิดหน้าต่าง
                </Button>,
                <Button
                    key="print"
                    type="primary"
                    icon={<FontAwesomeIcon icon="print" style={{ marginRight: '6px' }} />}
                    onClick={handlePrint}
                    style={{
                        background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 700
                    }}
                    data-testid="btn-print-voucher"
                >
                    พิมพ์ใบสำคัญจ่าย
                </Button>
            ]}
            data-testid="expense-voucher-modal"
        >
            <div className="expense-voucher-print-area" ref={voucherRef} data-testid="expense-voucher-document">
                <div className="expense-voucher-paper">
                    {/* Header */}
                    <div className="voucher-header">
                        <div>
                            <div className="voucher-store-name">🏪 JinDa Retail POS</div>
                            <div className="voucher-store-address">
                                123/45 ถนนพหลโยธิน แขวงลาดยาว เขตจตุจักร กรุงเทพมหานคร 10900<br />
                                โทรศัพท์: 02-123-4567 | เลขประจำตัวผู้เสียภาษี: 0105559999999
                            </div>
                        </div>
                        <div className="voucher-title-box">
                            <div className="voucher-main-title">ใบสำคัญจ่ายเงิน</div>
                            <div style={{ fontSize: '13px', color: '#64748B', fontWeight: 600 }}>PAYMENT VOUCHER</div>
                            <div className="voucher-doc-no" style={{ marginTop: '4px' }}>{expense.doc_no || 'EXP-TEMP'}</div>
                        </div>
                    </div>

                    {/* Metadata Grid */}
                    <div className="voucher-meta-grid">
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">วันที่จ่าย:</span>
                            <span className="voucher-meta-value">{expense.expense_date || '-'}</span>
                        </div>
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">หมวดหมู่ค่าใช้จ่าย:</span>
                            <span className="voucher-meta-value">{expense.category_name || '-'}</span>
                        </div>
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">จ่ายให้แก่ (Payee):</span>
                            <span className="voucher-meta-value">{expense.payee || 'เงินสด / บุคคลทั่วไป'}</span>
                        </div>
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">ช่องทางชำระเงิน:</span>
                            <span className="voucher-meta-value">{expense.payment_type_name || 'เงินสด'}</span>
                        </div>
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">ผู้บันทึกรายการ:</span>
                            <span className="voucher-meta-value">{expense.recorded_by || '-'}</span>
                        </div>
                        <div className="voucher-meta-item">
                            <span className="voucher-meta-label">สถานะเอกสาร:</span>
                            <span className="voucher-meta-value" style={{ color: expense.status === 1 ? '#10B981' : '#EF4444' }}>
                                {expense.status === 1 ? 'อนุมัติจ่ายแล้ว' : 'ยกเลิก'}
                            </span>
                        </div>
                    </div>

                    {/* Expense Table Breakdown */}
                    <table className="voucher-table">
                        <thead>
                            <tr>
                                <th style={{ width: '50px', textAlign: 'center' }}>ลำดับ</th>
                                <th>รายการค่าใช้จ่าย / คำอธิบาย</th>
                                <th style={{ width: '140px', textAlign: 'right' }}>จำนวนเงิน (บาท)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ textAlign: 'center' }}>1</td>
                                <td>
                                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{expense.title || '-'}</div>
                                    {expense.remark && (
                                        <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                                            หมายเหตุ: {expense.remark}
                                        </div>
                                    )}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                    {formatNumber(expense.amount || 0)}
                                </td>
                            </tr>
                            {/* Summary Rows */}
                            <tr>
                                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600, background: '#F8FAFC' }}>
                                    ยอดรวมก่อนภาษี (Subtotal):
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600, background: '#F8FAFC' }}>
                                    {formatNumber(expense.amount || 0)}
                                </td>
                            </tr>
                            {expense.vat_amount > 0 && (
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'right', color: '#64748B' }}>
                                        ภาษีมูลค่าเพิ่ม (VAT 7%):
                                    </td>
                                    <td style={{ textAlign: 'right', color: '#64748B' }}>
                                        +{formatNumber(expense.vat_amount)}
                                    </td>
                                </tr>
                            )}
                            {expense.wht_amount && expense.wht_amount > 0 ? (
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'right', color: '#EF4444' }}>
                                        หัก ณ ที่จ่าย ({expense.wht_rate || 3}%):
                                    </td>
                                    <td style={{ textAlign: 'right', color: '#EF4444' }}>
                                        -{formatNumber(expense.wht_amount)}
                                    </td>
                                </tr>
                            ) : null}
                            <tr style={{ background: '#FFF9F6' }}>
                                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 800, fontSize: '15px', color: '#FF7A59' }}>
                                    ยอดจ่ายสุทธิ (Net Total):
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '16px', color: '#FF7A59' }}>
                                    ฿{formatNumber(expense.net_amount || 0)}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Thai Baht text description */}
                    <div style={{
                        background: '#F1F5F9',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#334155',
                        marginBottom: '30px'
                    }}>
                        จำนวนเงินตัวอักษร: ({thaiBahtText(expense.net_amount || 0)})
                    </div>

                    {/* Signatures */}
                    <div className="voucher-signatures">
                        <div className="voucher-sig-box">
                            <div className="voucher-sig-line" />
                            <div style={{ fontWeight: 700 }}>( {expense.payee || '...........................................'} )</div>
                            <div>ผู้รับเงิน / ผู้เบิกจ่าย</div>
                            <div style={{ fontSize: '11px', marginTop: '4px' }}>วันที่ ......./......./............</div>
                        </div>

                        <div className="voucher-sig-box">
                            <div className="voucher-sig-line" />
                            <div style={{ fontWeight: 700 }}>( {expense.recorded_by || '...........................................'} )</div>
                            <div>ผู้จ่ายเงิน / เจ้าหน้าที่การเงิน</div>
                            <div style={{ fontSize: '11px', marginTop: '4px' }}>วันที่ ......./......./............</div>
                        </div>

                        <div className="voucher-sig-box">
                            <div className="voucher-sig-line" />
                            <div style={{ fontWeight: 700 }}>( ........................................... )</div>
                            <div>ผู้มีอำนาจอนุมัติ</div>
                            <div style={{ fontSize: '11px', marginTop: '4px' }}>วันที่ ......./......./............</div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ExpenseVoucherPrint;
